import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { map, Observable, shareReplay } from 'rxjs';

import { SubscriptionAction } from '@blaze-shared/datasets-data-access';
import {
  getBackground,
  getBorder,
  NotificationButtonAlertConfig,
  NotificationButtonType,
} from '@blaze-shared/elements-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { SubscriptionService } from '../../services/subscription.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

@Component({
  selector: 'be-notification-button',
  templateUrl: './notification-button.component.html',
  styleUrls: ['./notification-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NotificationButtonComponent
  extends CanvasElementBaseComponent<'notificationButton'>
  implements CanvasElementComponent<'notificationButton'>, OnChanges
{
  private readonly subscriptionService = inject(SubscriptionService);

  @HostBinding('style.--b-nb-counter-color') headerColor = '#fff';
  @HostBinding('style.--b-nb-label-color') titleColor = '#333';
  @HostBinding('style.--b-nb-button-background') buttonBackground = 'none';
  @HostBinding('style.--b-nb-button-border') buttonBorder = 'none';
  @HostBinding('style.--b-nb-button-border-radius') buttonBorderRadius = '0px';
  @HostBinding('style.--b-nb-popover-color') popoverColor = '#00f';
  @HostBinding('style.--b-nb-popover-border') popoverBorder = '#00f';
  @HostBinding('style.--b-nb-popover-background') popoverBackground = '#fff';

  readonly MAX_COUNT = 99;

  type?: NotificationButtonType;
  labelEnabled = false;
  labelText = '';
  notificationIcon = '';
  count$?: Observable<string>;

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value || changes.props) {
      this.updateStyles();
    }
    if (changes.props) {
      const props = changes.props.currentValue as typeof this.props;
      this.updateAlert(props?.content.alert);
    }
  }

  emitClick(toolbarTriggered = false): void {
    if (!this.isInEditor || toolbarTriggered) {
      this.output.emit({
        eventType: 'click',
        data: undefined,
        schema: null,
        isInitialData: false,
      });
    }
  }

  private updateAlert(alert: NotificationButtonAlertConfig | undefined): void {
    if (alert?.active && alert.id) {
      this.count$ ||= this.subscriptionService
        .subscribeFor<string>(alert.id, SubscriptionAction.Count)
        .pipe(
          map((count) => parseInt(count)),
          map((count) => (count > this.MAX_COUNT ? `${this.MAX_COUNT}+` : count.toString())),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
    } else {
      this.count$ &&= undefined;
    }
  }

  private updateStyles(): void {
    const { content, style } = this.props || {};
    const { notificationButton, title } = content || {};
    const { general, button, label, labelBlock } = style || {};
    if (!general || !button || !label || !labelBlock) {
      return;
    }

    this.type = notificationButton?.type;
    this.labelEnabled = title?.enabled ?? true;
    this.labelText = title?.label ?? '';
    this.notificationIcon = this.type === 'icon_with_counter' ? notificationButton?.icon || '' : '';
    this.headerColor = label.counter.color1;
    this.titleColor = label.label.color1;
    this.buttonBackground = getBackground(button.fill);
    this.buttonBorder = getBorder(button);
    this.buttonBorderRadius = button.radius;
    this.popoverColor = button.fill.color1;
    this.popoverBorder =
      button.outline.selectorType !== 'none' ? button.outline.color1 : button.fill.color1;
    this.popoverBackground = label.counter.color1;
  }
}
